/**
 * CandidateSummaryCard — compact person card used in the intake result panels
 * (linked / ambiguous) and anywhere a candidate needs a quick summary.
 */

import React from 'react';
import { Phone, Mail, MapPin, Briefcase } from 'lucide-react';
import { cn } from '../../../utils/cn';
import type { Candidate } from '../../../services/recruitmentApi';
import { Avatar } from './badges';
import { formatExperience } from '../utils/format';

interface CandidateSummaryCardProps {
	candidate: Candidate;
	selected?: boolean;
	onClick?: () => void;
	footer?: React.ReactNode;
	className?: string;
}

export const CandidateSummaryCard: React.FC<CandidateSummaryCardProps> = ({ candidate, selected, onClick, footer, className }) => {
	const clickable = !!onClick;
	const roleLine = [candidate.current_role, candidate.current_company].filter(Boolean).join(' · ');

	return (
		<div
			role={clickable ? 'button' : undefined}
			tabIndex={clickable ? 0 : undefined}
			onClick={onClick}
			onKeyDown={clickable ? e => (e.key === 'Enter' || e.key === ' ') && onClick?.() : undefined}
			className={cn(
				'rounded-xl border bg-white p-4 text-left transition',
				clickable && 'cursor-pointer hover:border-primary/50 hover:shadow-sm',
				selected ? 'border-primary ring-1 ring-primary' : 'border-gray-200',
				className
			)}
		>
			<div className='flex items-start gap-3'>
				<Avatar name={candidate.full_name} />
				<div className='min-w-0 flex-1'>
					<p className='truncate text-sm font-semibold text-gray-900'>{candidate.full_name}</p>
					{roleLine && (
						<p className='mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500'>
							<Briefcase className='h-3 w-3' /> {roleLine}
						</p>
					)}
					<div className='mt-2 space-y-1 text-xs text-gray-600'>
						{candidate.phone_raw && (
							<p className='flex items-center gap-1.5'>
								<Phone className='h-3 w-3 text-gray-400' /> {candidate.phone_raw}
							</p>
						)}
						{candidate.email_raw && (
							<p className='flex items-center gap-1.5'>
								<Mail className='h-3 w-3 text-gray-400' /> {candidate.email_raw}
							</p>
						)}
						{candidate.location && (
							<p className='flex items-center gap-1.5'>
								<MapPin className='h-3 w-3 text-gray-400' /> {candidate.location}
							</p>
						)}
						<p className='text-gray-400'>Experience: {formatExperience(candidate.total_experience)}</p>
					</div>
				</div>
			</div>
			{footer && <div className='mt-3 border-t border-gray-100 pt-3'>{footer}</div>}
		</div>
	);
};
